import { ReactNode, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { Dialog as Primitive } from 'radix-ui';

import { Button } from '@components/primitives/Button';
import { DialogHeader } from '@components/primitives/Dialog/DialogHeader';
import { DialogLock } from '@components/primitives/Dialog/DialogLock';
import { cn, easeOutSmooth } from '@components/primitives/utilities';

import { DialogBody } from './DialogBody';
import { DialogConfirmation } from './DialogConfirmation';
import { DialogContent } from './DialogContent';
import { DialogFooter } from './DialogFooter';
import { DialogOverlay } from './DialogOverlay';
import { DialogRoot } from './DialogRoot';
import { DialogState } from './useDialog';

import css from './Dialog.module.css';
export type { DialogState } from './useDialog';
export { default as useDialog } from './useDialog';

export interface DialogProps<T = void> {
  state: DialogState<T>;
  trigger?: string | ReactNode;
  className?: string;
  closeButton?: boolean;
  children: ReactNode | ((data: T) => ReactNode);
}

function Dialog<T = void>({
  state,
  children,
  className,
  trigger,
  closeButton,
}: DialogProps<T>) {
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // the panel stays mounted while it animates closed, so keep the last payload
  const data = useRef(state.data as T);
  if (state.isOpen) {
    data.current = state.data;
  }

  return (
    <DialogRoot state={state} trigger={trigger}>
      <DialogOverlay
        className={css.overlay}
        {...OVERLAY_ANIMATIONS}
        onClick={handleOverlayClick}
      >
        <DialogContent
          className={cn(css.window, className)}
          {...WINDOW_ANIMATIONS}
        >
          <DialogChildren data={data.current}>{children}</DialogChildren>

          {closeButton && (
            <Primitive.Close asChild data-id="dialog-close">
              <Button size="xs" className={css.close}>
                <X />
              </Button>
            </Primitive.Close>
          )}
        </DialogContent>
      </DialogOverlay>
    </DialogRoot>
  );
}

/** Kept separate so a render-prop child only runs while the panel is mounted. */
function DialogChildren<T>({
  data,
  children,
}: {
  data: T;
  children: ReactNode | ((data: T) => ReactNode);
}) {
  return <>{typeof children === 'function' ? children(data) : children}</>;
}

const OVERLAY_ANIMATIONS = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.15 },
};

const WINDOW_ANIMATIONS = {
  initial: { y: 12, scale: 0.99, rotateX: -2 },
  animate: { y: 0, scale: 1, rotateX: 0 },
  exit: {
    y: 12,
    scale: 0.99,
    rotateX: -2,
    transition: { duration: 0.15, ease: easeOutSmooth },
  },
  transition: { duration: 0.2, ease: easeOutSmooth },
};

const DialogNamespace = Object.assign(Dialog, {
  Root: DialogRoot,
  Overlay: DialogOverlay,
  Content: DialogContent,
  Header: DialogHeader,
  Body: DialogBody,
  Footer: DialogFooter,
  Title: Primitive.Title,
  Description: Primitive.Description,
  Close: Primitive.Close,
  Confirmation: DialogConfirmation,
  Lock: DialogLock,
});

export { DialogNamespace as Dialog };
export * from './context';
